import { ArrowRight, Sparkles, Wand2, Zap, Globe, Flame, CheckCircle2, Upload, Download, Clock, FileText, Loader2, Plus } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { getUserResumes } from '../services/resumeService.js';
import AdBanner from './ui/AdBanner.jsx';
import { useSubscription } from '../contexts/SubscriptionContext.jsx';
import SEO from './SEO.jsx';

const LandingPage = ({ onStart, currentUser, onNavigate, onLoadResume }) => {
    const { isPremium } = useSubscription();
    const [recentResumes, setRecentResumes] = useState([]);
    const [loadingResumes, setLoadingResumes] = useState(false);

    useEffect(() => {
        if (currentUser) {
            setLoadingResumes(true);
            getUserResumes(currentUser.uid)
                .then(resumes => {
                    // Sort by updatedAt desc
                    setRecentResumes(resumes.sort((a, b) => b.updatedAt?.seconds - a.updatedAt?.seconds).slice(0, 3));
                })
                .catch(err => console.error("Failed to fetch resumes:", err))
                .finally(() => setLoadingResumes(false));
        }
    }, [currentUser]);

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-slate-100 overflow-x-hidden selection:bg-blue-500 selection:text-white">
            <SEO
                title="Resume Architect - Free AI Resume Builder"
                description="Create professional resumes in seconds with our AI Resume Builder. Free templates, instant translations, and ATS optimization."
                keywords="resume builder, AI resume, free resume maker, CV builder, resume templates"
            />
            {/* Dynamic Background */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[120px] animate-blob" />
                <div className="absolute top-[20%] right-[-10%] w-[600px] h-[600px] bg-purple-600/20 rounded-full blur-[120px] animate-blob animation-delay-2000" />
                <div className="absolute bottom-[-10%] left-[20%] w-[500px] h-[500px] bg-pink-600/20 rounded-full blur-[120px] animate-blob animation-delay-4000" />
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay"></div>
            </div>

            {/* Navigation */}
            <nav className="fixed w-full z-50 transition-all duration-300 border-b border-white/5 bg-slate-900/50 backdrop-blur-md">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="relative group cursor-pointer" onClick={onStart}>
                            <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg blur opacity-25 group-hover:opacity-60 transition duration-500"></div>
                            <div className="relative bg-slate-900 p-2 rounded-lg border border-slate-700/50">
                                <Zap className="text-blue-400 group-hover:text-blue-300 transition-colors" size={24} />
                            </div>
                        </div>
                        <span className="font-bold text-xl tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">
                            Resume Architect
                        </span>
                    </div>
                    <div>
                        <button
                            onClick={onStart}
                            className="text-sm font-medium text-slate-300 hover:text-white transition-colors relative group px-2 py-1"
                        >
                            {currentUser ? `Hi, ${currentUser.email?.split('@')[0]}` : 'Sign In'}
                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 transition-all group-hover:w-full"></span>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Dashboard Section (Logged In) */}
            {currentUser && (
                <section className="pt-32 pb-4 relative z-10 animate-fade-in-up">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                                <Clock size={24} className="text-blue-400" />
                                Your Recent Resumes
                            </h2>
                            <button
                                onClick={onStart}
                                className="text-sm font-medium text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors"
                            >
                                <Plus size={16} /> New Resume
                            </button>
                        </div>

                        {loadingResumes ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 size={32} className="text-blue-500 animate-spin" />
                            </div>
                        ) : recentResumes.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {recentResumes.map(resume => (
                                    <div
                                        key={resume.id}
                                        onClick={() => onLoadResume(resume)}
                                        className="group bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 hover:border-blue-500/50 rounded-xl p-5 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-900/10"
                                    >
                                        <div className="flex justify-between items-start mb-4">
                                            <div className="bg-blue-500/10 p-2.5 rounded-lg group-hover:bg-blue-500/20 transition-colors">
                                                <FileText className="text-blue-400 group-hover:scale-110 transition-transform" size={20} />
                                            </div>
                                            <span className="text-xs font-mono text-slate-500 bg-slate-900/50 px-2 py-1 rounded">
                                                {resume.updatedAt?.seconds
                                                    ? new Date(resume.updatedAt.seconds * 1000).toLocaleDateString()
                                                    : 'Just now'}
                                            </span>
                                        </div>
                                        <h3 className="text-lg font-bold text-slate-200 group-hover:text-blue-400 transition-colors truncate mb-1">
                                            {resume.title || 'Untitled Resume'}
                                        </h3>
                                        <p className="text-sm text-slate-500 truncate">
                                            {JSON.parse(resume.data).personal?.role || "No Role Defined"}
                                        </p>
                                    </div>
                                ))}
                                <div
                                    onClick={onStart}
                                    className="group bg-slate-800/30 hover:bg-slate-800/50 border border-dashed border-slate-700 hover:border-slate-500 rounded-xl p-5 cursor-pointer transition-all flex flex-col items-center justify-center text-slate-500 hover:text-slate-300 min-h-[140px]"
                                >
                                    <Plus size={32} className="mb-2 group-hover:scale-110 transition-transform" />
                                    <span className="text-sm font-medium">Create New</span>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-slate-800/30 border border-dashed border-slate-700 rounded-xl p-8 text-center text-slate-500">
                                <p className="mb-4">No resumes found yet.</p>
                                <button
                                    onClick={onStart}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors inline-flex items-center gap-2"
                                >
                                    <Plus size={16} /> Create First Resume
                                </button>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Hero Section (Condensed if logged in) */}
            <section className={`relative transition-all ${currentUser ? 'py-12 lg:py-20 opacity-90 scale-95' : 'pt-32 pb-20 lg:pt-48 lg:pb-32'} overflow-visible`}>
                <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center relative z-10">


                    {/* Left: Content */}
                    <div className="text-left">
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-8 animate-fade-in-up backdrop-blur-md">
                            <Sparkles size={14} className="animate-pulse" />
                            <span className="tracking-wide text-xs uppercase font-bold">New AI Engine V2.0</span>
                        </div>

                        <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-white mb-6 leading-[1.1] drop-shadow-2xl">
                            Make your Resume in <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400">
                                Seconds
                            </span>
                            <span className="text-slate-600 line-through text-3xl md:text-5xl ml-4 decoration-red-500/50 decoration-2 font-medium">Hours</span>
                        </h1>

                        <p className="text-lg md:text-xl text-slate-400 mb-10 leading-relaxed font-light max-w-lg">
                            Stop fighting with formatting. Our <span className="text-blue-300 font-medium">AI Architect</span> builds, optimizes, and tailors your resume to beat the ATS and get you hired.
                        </p>

                        <div className="flex flex-col sm:flex-row items-center gap-4">
                            <button
                                onClick={onStart}
                                className="group w-full sm:w-auto px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-lg transition-all transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/25 flex items-center justify-center gap-2"
                            >
                                Build My Resume
                                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                            </button>
                        </div>

                        <p className="mt-6 text-sm text-slate-400 font-light flex items-center gap-2 animate-fade-in-up delay-200">
                            <Sparkles size={14} className="text-blue-400" />
                            <span className="opacity-80">
                                <strong>Pro tip:</strong> Paste from LinkedIn/PDF for speed, or <span className="text-white font-medium">start fresh</span> if you don't have a resume yet.
                            </span>
                        </p>

                        <div className="mt-8 flex items-center gap-4 text-sm text-slate-500">
                            <div className="flex -space-x-2">
                                {[1, 2, 3, 4].map(i => (
                                    <div key={i} className="w-8 h-8 rounded-full border-2 border-slate-900 bg-slate-800 flex items-center justify-center text-[10px] text-white font-bold">
                                        {String.fromCharCode(64 + i)}
                                    </div>
                                ))}
                            </div>
                            <p>Trusted by 10,000+ job seekers</p>
                        </div>
                    </div>

                    {/* Right: Visual (3D Stack) */}
                    <div className="relative hidden lg:block perspective-1000 group">
                        {/* Background Decoration */}
                        <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 to-purple-500/20 rounded-full blur-[100px] -z-10 transform translate-x-20"></div>

                        {/* Card 1 (Back) */}
                        <div className="absolute top-[-40px] right-[-40px] w-full h-[500px] bg-slate-800 rounded-2xl border border-slate-700/50 transform rotate-6 scale-90 opacity-40 transition-transform duration-700 group-hover:rotate-12 group-hover:translate-x-10"></div>

                        {/* Card 2 (Middle) */}
                        <div className="absolute top-[-20px] right-[-20px] w-full h-[500px] bg-slate-800 rounded-2xl border border-slate-700/50 transform rotate-3 scale-95 opacity-70 transition-transform duration-700 group-hover:rotate-6 group-hover:translate-x-5 shadow-2xl"></div>

                        {/* Card 3 (Front - Main UI) */}
                        <div className="relative w-full h-[500px] bg-slate-900 rounded-2xl border border-slate-700/50 shadow-2xl overflow-hidden flex flex-col transform transition-transform duration-700 group-hover:scale-[1.02] -rotate-1">
                            {/* Fake Browser Header */}
                            <div className="h-10 border-b border-slate-800 flex items-center px-4 gap-2 bg-slate-900/50">
                                <div className="w-3 h-3 rounded-full bg-red-500/50"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-500/50"></div>
                                <div className="w-3 h-3 rounded-full bg-green-500/50"></div>
                                <div className="ml-4 flex-1 h-5 bg-slate-800 rounded-full w-1/2 opacity-50"></div>
                            </div>

                            {/* Fake Content */}
                            <div className="p-6 flex-1 flex gap-6">
                                {/* Sidebar */}
                                <div className="w-1/4 space-y-3">
                                    <div className="h-20 bg-slate-800/50 rounded-lg animate-pulse"></div>
                                    <div className="h-8 bg-slate-800/30 rounded-lg"></div>
                                    <div className="h-8 bg-slate-800/30 rounded-lg"></div>
                                    <div className="h-8 bg-slate-800/30 rounded-lg"></div>
                                </div>
                                {/* Main Editor */}
                                <div className="flex-1 space-y-4">
                                    <div className="h-8 w-1/3 bg-blue-500/20 rounded-lg mb-6"></div>
                                    <div className="space-y-4">
                                        <div className="h-24 bg-slate-800/50 rounded-lg border border-slate-700/50 p-4">
                                            <div className="h-4 w-1/4 bg-slate-700 rounded mb-2"></div>
                                            <div className="h-2 w-full bg-slate-700/50 rounded"></div>
                                            <div className="h-2 w-3/4 bg-slate-700/50 rounded mt-2"></div>
                                        </div>
                                        <div className="h-24 bg-slate-800/50 rounded-lg border border-slate-700/50 p-4 flex items-center justify-center relative overflow-hidden">
                                            <div className="absolute inset-0 bg-blue-500/5 animate-pulse"></div>
                                            <Wand2 className="text-blue-400 mb-2 mr-2" size={20} />
                                            <span className="text-blue-300 text-sm font-medium">AI Writing...</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Floating Badge */}
                            <div className="absolute bottom-8 right-8 bg-gradient-to-r from-blue-600 to-purple-600 p-4 rounded-xl shadow-lg border border-white/10 animate-float">
                                <div className="flex items-center gap-3">
                                    <div className="bg-white/20 p-2 rounded-lg">
                                        <CheckCircle2 className="text-white" size={20} />
                                    </div>
                                    <div>
                                        <p className="text-white font-bold text-sm">ATS Score</p>
                                        <p className="text-blue-100 text-xs">98/100 Optimized</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-24 relative z-10 bg-slate-900/50">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">How It Works</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">From blank page to hired in three simple steps.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 relative">
                        {/* Connecting Line (Desktop) */}
                        <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-blue-900 via-purple-900 to-blue-900 -z-10"></div>

                        {[
                            { icon: Upload, title: "1. Start", desc: "Upload existing resume or start fresh." },
                            { icon: Wand2, title: "2. AI Enhance", desc: "Let AI rewrite and optimize your content." },
                            { icon: Download, title: "3. Get Hired", desc: "Export perfect PDF and apply." }
                        ].map((step, idx) => (
                            <div key={idx} className="relative flex flex-col items-center text-center group">
                                <div className="w-24 h-24 bg-slate-800 rounded-2xl border border-slate-700 flex items-center justify-center mb-6 shadow-xl group-hover:-translate-y-2 transition-transform duration-300 z-10 relative overflow-hidden">
                                    <div className="absolute inset-0 bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors"></div>
                                    <step.icon size={32} className="text-blue-400 group-hover:scale-110 transition-transform duration-300" />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                                <p className="text-slate-400 text-sm">{step.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Ad Banner (Middle) */}
            {!isPremium && <AdBanner slotId={process.env.REACT_APP_ADSENSE_SLOT_ID || "1234567890"} style={{ maxWidth: '800px', margin: '2rem auto' }} />}

            {/* Feature Grid */}
            <section className="py-32 relative z-10">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">Everything You Need</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">Packed with premium tools usually reserved for career coaches.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Feature 1 */}
                        <div className="group p-8 rounded-3xl glass-dark hover:bg-slate-800/80 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-blue-500/10 border border-slate-700/50">
                            <div className="w-14 h-14 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 border border-blue-500/20 group-hover:border-blue-500/50">
                                <Wand2 className="text-blue-400 group-hover:rotate-12 transition-transform duration-500" size={28} />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-3">AI Smart Writer</h3>
                            <p className="text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">
                                Writer's block is dead. Generate professional summaries and punchy experience bullet points instantly.
                            </p>
                        </div>

                        {/* Feature 2 */}
                        <div className="group p-8 rounded-3xl glass-dark hover:bg-slate-800/80 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-purple-500/10 border border-slate-700/50">
                            <div className="w-14 h-14 bg-purple-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 border border-purple-500/20 group-hover:border-purple-500/50">
                                <Globe className="text-purple-400 group-hover:rotate-12 transition-transform duration-500" size={28} />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-3">Global Translation</h3>
                            <p className="text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">
                                Applying abroad? Translate your entire resume into languages like Japanese, French, or Spanish instantly.
                            </p>
                        </div>

                        {/* Feature 3 */}
                        <div className="group p-8 rounded-3xl glass-dark hover:bg-slate-800/80 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-red-500/10 border border-slate-700/50">
                            <div className="w-14 h-14 bg-red-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 border border-red-500/20 group-hover:border-red-500/50">
                                <Flame className="text-red-400 group-hover:rotate-12 transition-transform duration-500" size={28} />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-3">The Roaster</h3>
                            <p className="text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">
                                Need a reality check? Our "Roaster" AI gives you a brutal score and honest feedback to help you improve.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Ad Banner (Bottom) */}
            {!isPremium && <AdBanner slotId={process.env.REACT_APP_ADSENSE_SLOT_ID || "1234567890"} style={{ maxWidth: '800px', margin: '2rem auto' }} />}

            {/* Footer */}
            <footer className="py-12 border-t border-slate-800/50 bg-slate-900/50 backdrop-blur-xl">
                <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6 text-slate-500 text-sm">
                    <p onClick={(e) => {
                        if (e.detail === 3) { // Triple click to be safe/secretive
                            onNavigate('admin');
                        }
                    }} className="cursor-default select-none transition-colors hover:text-slate-400">
                        © 2025 Resume Architect. <span className="text-slate-400">Built for the ambitious.</span>
                    </p>
                    <div className="flex flex-col md:flex-row items-center gap-8">
                        <button onClick={() => onNavigate('privacy')} className="hover:text-blue-400 cursor-pointer transition-colors bg-transparent border-none p-0">Privacy</button>
                        <button onClick={() => onNavigate('terms')} className="hover:text-blue-400 cursor-pointer transition-colors bg-transparent border-none p-0">Terms</button>
                        <span className="flex items-center gap-2">
                            Support:
                            <a href="mailto:resume.architect.pro.help@gmail.com" className="text-slate-400 hover:text-white transition-colors">
                                resume.architect.pro.help@gmail.com
                            </a>
                        </span>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
